import {
  createContext,
  useContext,
  useEffect,
  type ReactNode,
} from 'react';
import { Auth0Provider, useAuth0 } from '@auth0/auth0-react';
import { config, isAuthEnabled } from '../runtime-config';
import { setTokenProvider } from '../api/client';

/**
 * A single auth context that works in two modes:
 *   - Auth enabled (deployed): backed by @auth0/auth0-react.
 *   - Auth disabled (local dev): pass-through; getToken() returns undefined and the
 *     API is reached via `sam local --disable-authorizer`.
 *
 * Components consume `useAuthContext()` and never call useAuth0() directly, so the
 * page renders identically in both modes.
 */
export interface AuthContextValue {
  authEnabled: boolean;
  isAuthenticated: boolean;
  isLoading: boolean;
  user: { name?: string; email?: string; nickname?: string } | null;
  getToken: () => Promise<string | undefined>;
  login: () => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue>({
  authEnabled: false,
  isAuthenticated: true,
  isLoading: false,
  user: null,
  getToken: async () => undefined,
  login: () => {},
  logout: () => {},
});

export const useAuthContext = () => useContext(AuthContext);

function Auth0Bridge({ children }: { children: ReactNode }) {
  const {
    isAuthenticated,
    isLoading,
    user,
    getAccessTokenSilently,
    loginWithRedirect,
    logout,
  } = useAuth0();

  // Register the bearer-token getter so the API client can authenticate requests.
  useEffect(() => {
    setTokenProvider(async () => {
      try {
        return await getAccessTokenSilently();
      } catch {
        return undefined;
      }
    });
  }, [getAccessTokenSilently]);

  const value: AuthContextValue = {
    authEnabled: true,
    isAuthenticated,
    isLoading,
    user: (user as AuthContextValue['user']) ?? null,
    getToken: async () => {
      try {
        return await getAccessTokenSilently();
      } catch {
        return undefined;
      }
    },
    login: () => void loginWithRedirect(),
    logout: () => logout({ logoutParams: { returnTo: window.location.origin } }),
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * Used when this app is federated into a host shell (e.g. naviance-admin-tools-v2)
 * that owns its own Auth0 login/session and hands us an already-fetched access
 * token as a prop, instead of us running our own Auth0Provider/login flow.
 *
 * The host is expected to re-fetch the token (via its own `getAccessTokenSilently`)
 * and re-render with a fresh `token` prop before/on expiry; we just always read the
 * latest prop value when the API client needs a bearer token.
 */
export function HostTokenAuthProvider({
  token,
  children,
}: {
  token: string | undefined;
  children: ReactNode;
}) {
  // Register synchronously so child effects that load the initial directory
  // cannot race this provider and send an unauthenticated request.
  setTokenProvider(async () => token?.trim() || undefined);

  const value: AuthContextValue = {
    authEnabled: true,
    isAuthenticated: !!token,
    isLoading: false,
    // The host owns the Auth0 user/session; we don't have direct access to it here.
    user: null,
    getToken: async () => token,
    // No-ops: login/logout are the host shell's responsibility in this mode.
    login: () => {},
    logout: () => {},
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function AppAuthProvider({ children }: { children: ReactNode }) {
  if (!isAuthEnabled) {
    const value: AuthContextValue = {
      authEnabled: false,
      isAuthenticated: true,
      isLoading: false,
      user: null,
      getToken: async () => undefined,
      login: () => {},
      logout: () => {},
    };
    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
  }

  return (
    <Auth0Provider
      domain={config.auth0Domain}
      clientId={config.auth0ClientId}
      cacheLocation="localstorage"
      authorizationParams={{
        redirect_uri: window.location.origin,
        ...(config.auth0Audience ? { audience: config.auth0Audience } : {}),
      }}
    >
      <Auth0Bridge>{children}</Auth0Bridge>
    </Auth0Provider>
  );
}
